// src/Dashboard.js

import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { auth, db } from "./firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const brollOptions = [
  { file: "declutter_home.mov", title: "Declutter to Close Fast", script: "If I wanted to sell my home in 30 days... here is what I'd do... Declutter, open up the space, create space for the buyer to envision, and ask for help! More in the captions!" },
  { file: "home_inspection.mp4", title: "Inspection Red Flags", script: "Don’t miss these 3 things during a home inspection... more in the caption!" },
  { file: "budget_first.mp4", title: "Afford Before You Adore", script: "Before you fall in love with a home, let’s talk numbers! Do you know what you can afford? There is one simple rule every buyer should know. Let’s talk about the 30% rule in the caption!" },
  { file: "supply_demand.mp4", title: "What Drives Prices", script: "Ever wondered what really drives home prices? It all comes down to supply and demand. More supply means lower prices, more demand means higher prices. Understanding this concept is key!" },
  { file: "interest_rate.mp4", title: "When Rates Rise...", script: "Interest rates change... and when it happens, it can shake up the market! So what does it mean for your homebuying plans? Tune in the captions for more!" },
  { file: "loan_types.mp4", title: "Which Loan is Right?", script: "Just the thought of choosing the right mortgage can feel overwhelming... but understanding your options is key to finding the perfect fit! Here are six types of loans. More in the caption below." },
  { file: "neighborhood_value.mp4", title: "Smart Neighborhood Picks", script: "Not all neighborhoods are created equal... don’t get caught in a declining area! Here are the key factors for evaluating growth potential!" },
  { file: "prelist_inspection.mp4", title: "Seller Inspection Advantage", script: "We talk a lot about the benefits of home buyer inspections. Let’s turn the tables and talk about how seller pre-list inspections can be a real game changer when selling. More in the captions below!" },
  { file: "buyer_anxiety.mp4", title: "Dream Home Anxiety", script: "Worried about buying your dream home? You’re not alone — it’s a big decision. I’m sharing a few expert tips to ease the stress and help you move forward with confidence. More in the caption below!" }
];

export default function Dashboard() {
  const [agentName, setAgentName] = useState("");
  const [agentEmail, setAgentEmail] = useState("");
  const [voiceFile, setVoiceFile] = useState(null);
  const [selectedBroll, setSelectedBroll] = useState([]);
  const [downloadLinks, setDownloadLinks] = useState([]);
  const [usageBlocked, setUsageBlocked] = useState(false);
  const [usageMessage, setUsageMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [rating, setRating] = useState("");
  const [comment, setComment] = useState("");
  const [recommend, setRecommend] = useState("");
  const [userUID, setUserUID] = useState(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) setUserUID(user.uid);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const checkLastUsage = async () => {
      const user = auth.currentUser;
      if (!user) return;

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const email = userSnap.data().email?.toLowerCase();
        if (email === "realsautomated@gmail.com") return;

        const lastUsed = userSnap.data().lastUsed;
        if (lastUsed?.toDate) {
          const lastUsedDate = lastUsed.toDate();
          const now = new Date();
          const diffDays = Math.floor((now - lastUsedDate) / (1000 * 60 * 60 * 24));
          if (diffDays < 7) {
            setUsageBlocked(true);
            setUsageMessage(`⏳ You can only generate reels once every 7 days. Please come back in ${7 - diffDays} day(s).`);
          }
        }
      }
    };

    checkLastUsage();
  }, []);

  const toggleBroll = (brollFile) => {
    setSelectedBroll((prev) =>
      prev.includes(brollFile)
        ? prev.filter((item) => item !== brollFile)
        : [...prev, brollFile].slice(0, 7)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (usageBlocked) return alert(usageMessage);
    if (!voiceFile) return alert("❗ Please upload a voice sample for cloning.");
    if (selectedBroll.length !== 7) return alert("📹 Please select exactly 7 b-roll clips.");
    setIsLoading(true);

    const formData = new FormData();
    formData.append("name", agentName);
    formData.append("email", agentEmail);
    formData.append("voice", voiceFile);
    formData.append("selectedBroll", JSON.stringify(selectedBroll));

    try {
      const uploadRes = await fetch("https://reel-backend-y11o.onrender.com/upload", {
        method: "POST",
        body: formData
      });
      const uploadData = await uploadRes.json();
      alert(uploadData.message || "🎉 Upload complete!");

      const generateRes = await fetch("https://reel-backend-y11o.onrender.com/process-reels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agentName, agentEmail })
      });

      const generateData = await generateRes.json();
      if (generateData.downloadUrls && Array.isArray(generateData.downloadUrls)) {
        const total = generateData.downloadUrls.length;
        setDownloadLinks(generateData.downloadUrls);

        if (total === 7) {
          alert("✅ All 7 reels were generated successfully! Scroll down to download them.");
        } else if (total > 0) {
          alert(`⚠️ Only ${total} of 7 reels were generated. Scroll down to download what’s available.`);
        } else {
          alert("❌ All reel generation failed. Please try again.");
        }
      }

      const user = auth.currentUser;
      if (user) {
        await setDoc(doc(db, "users", user.uid), { lastUsed: serverTimestamp() }, { merge: true });
      }
    } catch (err) {
      console.error(err);
      alert("❌ Something went wrong during the upload or generation.");
    } finally {
      setIsLoading(false);
    }
  };

  const VoiceDrop = () => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: { "audio/mpeg": [".mp3"], "audio/wav": [".wav"] },
      maxFiles: 1,
      onDrop: (acceptedFiles) => setVoiceFile(acceptedFiles[0])
    });

    return (
      <div {...getRootProps()} className="border border-dashed border-gray-300 p-4 rounded-md bg-white text-sm shadow-sm hover:border-blue-500 cursor-pointer">
        <input {...getInputProps()} />
        {voiceFile ? <p className="text-green-600">🎤 Voice file ready: {voiceFile.name}</p> : <p>{isDragActive ? "Drop the voice file here..." : "🎤 Drag and drop a voice sample here, or click to upload (MP3, WAV)"}</p>}
        <p className="text-xs text-gray-500 mt-2">
          💡 <strong>Mobile users:</strong> Record your voice using your phone’s voice recording app.<br />
          • iPhone: Use the <em>Voice Memos</em> app → tap “Save” → share to <strong>Files</strong> → upload here.<br />
          • Android: Use the <em>Voice Recorder</em> app (or similar) → save → upload the .mp3/.m4a file here.
        </p>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="text-white text-lg animate-pulse">⏳ Generating your reels...</div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">🎬 Weekly Reel Generator</h1>

        {usageBlocked && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
            <p>{usageMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-semibold text-gray-700 mb-1">Agent Name</label>
            <input className="w-full border p-2 rounded-md shadow-sm" value={agentName} onChange={(e) => setAgentName(e.target.value)} required />
          </div>

          <div>
            <label className="block font-semibold text-gray-700 mb-1">Email</label>
            <input type="email" className="w-full border p-2 rounded-md shadow-sm" value={agentEmail} onChange={(e) => setAgentEmail(e.target.value)} required />
          </div>

          <div>
            <label className="block font-semibold text-gray-700 mb-1">Upload Voice Sample for Cloning</label>
            <VoiceDrop />
            <a
              href="/Voice_Sample_Instructions.pdf"
              download
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 text-sm underline block mt-2"
            >
              📥 Download Voice Sample Instructions
            </a>
          </div>

          <div>
            <label className="block font-semibold text-gray-700 mb-2">Select 7 B-Roll Clips</label>
            <div className="grid md:grid-cols-3 gap-3">
              {brollOptions.map(({ file, title, script }) => (
                <button key={file} type="button" onClick={() => toggleBroll(file)} className={`border rounded-lg p-3 text-left shadow-sm transition-all ${selectedBroll.includes(file) ? "bg-blue-50 border-blue-400" : "hover:bg-gray-50"}`}>
                  🎥 <strong>{title}</strong>
                  <br />
                  <span className="text-xs text-gray-600">{script.slice(0, 60)}...</span>
                </button>
              ))}
            </div>
          </div>

          <button type="submit" disabled={isLoading} className={`w-full px-6 py-3 rounded-md font-semibold text-white ${isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}>
            {isLoading ? "Processing..." : "✅ Generate Reels"}
          </button>
        </form>

        {downloadLinks.length > 0 && (
          <div className="mt-10 p-6 bg-green-50 border rounded-xl shadow-sm">
            <h2 className="text-xl font-semibold text-green-800 mb-3">📥 Your Reels Are Ready!</h2>
            <ul className="list-disc pl-5 space-y-1">
              {downloadLinks.map((url, index) => (
                <li key={index}>
                  <a href={url} download target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Download Reel {index + 1}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {downloadLinks.length > 0 && showFeedbackForm && (
          <div className="mt-10 p-6 bg-yellow-50 border rounded-xl shadow-sm">
            <h2 className="text-xl font-semibold text-yellow-800 mb-3">💬 We’d love your feedback!</h2>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!rating || !recommend) {
                  alert("Please answer all required questions.");
                  return;
                }

                try {
                  const user = auth.currentUser;
                  const userSnap = await getDoc(doc(db, "users", user.uid));
                  const agentInfo = userSnap.data();

                  const feedbackId = `${userUID}_${Date.now()}`;
                  await setDoc(doc(db, "feedback", feedbackId), {
                    uid: userUID,
                    name: agentInfo?.name || agentName,
                    email: agentInfo?.email || agentEmail,
                    rating,
                    comment,
                    recommend,
                    timestamp: serverTimestamp()
                  });

                  alert("🎉 Thank you for your feedback!");
                  setShowFeedbackForm(false);
                } catch (err) {
                  console.error("Feedback error:", err);
                  alert("❌ Failed to submit feedback.");
                }
              }}
              className="space-y-4"
            >
              <div>
                <label className="block font-semibold">⭐ How was your experience?</label>
                <select
                  className="w-full border p-2 rounded-md"
                  value={rating}
                  onChange={(e) => setRating(e.target.value)}
                  required
                >
                  <option value="">Select rating</option>
                  <option value="1">1 - Poor</option>
                  <option value="2">2 - Fair</option>
                  <option value="3">3 - Good</option>
                  <option value="4">4 - Very Good</option>
                  <option value="5">5 - Excellent</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold">💡 Comments or suggestions?</label>
                <textarea
                  className="w-full p-2 border rounded-md"
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block font-semibold">📣 Would you recommend this to another agent?</label>
                <select
                  className="w-full border p-2 rounded-md"
                  value={recommend}
                  onChange={(e) => setRecommend(e.target.value)}
                  required
                >
                  <option value="">Select an option</option>
                  <option value="yes">Yes, definitely!</option>
                  <option value="maybe">Maybe</option>
                  <option value="no">Not really</option>
                </select>
              </div>

              <button type="submit" className="bg-yellow-600 text-white px-6 py-2 rounded-md hover:bg-yellow-700">
                ✅ Submit Feedback
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
